import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { createFrameCoalescer } from './frame_coalescer';

/**
 * React hook wrapper around createFrameCoalescer, for a callback that comes
 * from props/state and can change across renders (e.g. RangeSlider's
 * onValueChange). `send`/`flush` stay referentially stable across renders —
 * long-lived JSX handlers (onChange, onPointerUp, ...) don't need to be
 * re-bound — while always calling the latest callback. Any pending frame is
 * cancelled on unmount, and a send after unmount is a no-op.
 *
 * Callers that create their own gesture-scoped coalescer inside an imperative
 * event-handler closure (useLineDrag, useDragReorder) use
 * `createFrameCoalescer` directly instead: a hook can't be called there.
 */
export function useFrameThrottled<Args extends unknown[]>(
   callback: (...args: Args) => void
): { send: (...args: Args) => void; flush: () => void } {
   const callbackRef = useRef(callback);
   // Layout effect, not passive: a send can land between commit and the
   // passive-effect flush, and the frame it schedules must read the current
   // render's callback, not the previous one's.
   useLayoutEffect(() => {
      callbackRef.current = callback;
   });

   const coalescerRef = useRef<ReturnType<typeof createFrameCoalescer<Args>> | null>(null);
   const unmountedRef = useRef(false);
   useEffect(() => {
      // Reset on (re)mount: StrictMode runs the cleanup once on its
      // simulated unmount, and a stale true would kill every later send.
      unmountedRef.current = false;
      return () => {
         unmountedRef.current = true;
         coalescerRef.current?.cancel();
         coalescerRef.current = null;
      };
   }, []);

   // The coalescer is created lazily on the first send, not in a mount
   // effect: React commits the DOM before passive effects run, so an event
   // or a ref callback can call send in that window, and an effect-created
   // coalescer silently dropped those sends. send never runs during render,
   // so the lazy ref write stays out of render.
   const send = useCallback((...args: Args) => {
      if (unmountedRef.current) {
         return;
      }
      coalescerRef.current ??= createFrameCoalescer<Args>((...latest) =>
         callbackRef.current(...latest)
      );
      coalescerRef.current.send(...args);
   }, []);
   const flush = useCallback(() => coalescerRef.current?.flush(), []);

   return { send, flush };
}
